import { type ProductizedServiceCardPrice_price$key } from '#app/__generated__/relay/ProductizedServiceCardPrice_price.graphql.js';
import { Text } from '#app/components/Primitives/Text.js';
import { MailSendOutlineIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

const Container = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;

    ${theme.mediaQueries.lg} {
      align-items: center;
      padding: 24px;
      text-align: center;
    }
  `}
`;

const Price = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    ${theme.mediaQueries.lg} {
      flex-direction: column;
      gap: 0px;
    }
  `}
`;

const Label = styled(Text)`
  ${({ theme }) => css`
    line-height: 2;
    -webkit-line-clamp: 1;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;

    ${theme.mediaQueries.lg} {
      -webkit-line-clamp: 2;
    }
  `}
`;

type ProductizedServiceCardPriceProps = {
  readonly priceRef: ProductizedServiceCardPrice_price$key;
};

export const ProductizedServiceCardPrice = ({
  priceRef,
}: ProductizedServiceCardPriceProps) => {
  const { amount, interval, timeline, type } =
    useFragment<ProductizedServiceCardPrice_price$key>(
      graphql`
        fragment ProductizedServiceCardPrice_price on ProductizedServicePrice {
          amount
          interval
          timeline
          type
        }
      `,
      priceRef,
    );

  if (type === 'CONTACT_FOR_PRICING') {
    return (
      <Container>
        <Price>
          <MailSendOutlineIcon size={20} />
          <Text textStyle="subtitleRegular">Contact for pricing</Text>
        </Price>
      </Container>
    );
  }

  const numberFormat = Intl.NumberFormat('en-US');
  const formattedAmount = amount === null ? null : numberFormat.format(amount);
  const price = `$${formattedAmount}${
    interval ? `/${interval.toLowerCase()}` : ''
  }`;
  const rate =
    type === 'FIXED_PRICE'
      ? 'fixed rate'
      : interval === 'DAY'
        ? 'daily rate'
        : interval === 'HOUR'
          ? 'hourly rate'
          : interval === 'MONTH'
            ? 'monthly rate'
            : interval === 'WEEK'
              ? 'weekly rate'
              : null;
  const label = [rate, timeline].filter(Boolean).join(', ');

  return (
    <Container>
      <Text textStyle="overlineRegular">STARTING AT</Text>
      <Price>
        <Text textStyle="sixthLevelHeadline">{price}</Text>
        <Label textStyle="bodySmall">{label}</Label>
      </Price>
    </Container>
  );
};
